import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Brain,
  Layers,
  ShieldCheck,
  Code2,
  Cloud,
  Users,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useRegion } from '../../context/RegionContext';
import styles from './Services.module.css';

const serviceIcons = {
  software: Code2,
  cloud: Cloud,
  erp: Layers,
  ai: Brain,
  grc: ShieldCheck,
  changeManagement: Users,
};

const serviceColors: Record<string, string> = {
  software: '#10b981',      // Green
  cloud: '#3b82f6',         // Blue
  erp: '#f59e0b',           // Amber
  ai: '#8b5cf6',            // Purple
  grc: '#ef4444',           // Red
  changeManagement: '#06b6d4', // Cyan
};

const serviceLinks: Record<string, string> = {
  software: '/services/software-engineering',
  cloud: '/services/cloud-platform-foundations',
  erp: '/services/erp-sap',
  ai: '/services/ai-automation',
  grc: '/services/security-privacy-governance',
  changeManagement: '/services/change-management-delivery',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.15 },
  },
};

export const Services: React.FC = () => {
  const { t } = useLanguage();
  const { region } = useRegion();

  const services = [
    { key: 'software', icon: serviceIcons.software, ...t.services.software },
    { key: 'cloud', icon: serviceIcons.cloud, ...t.services.cloud },
    { key: 'erp', icon: serviceIcons.erp, ...t.services.erp },
    { key: 'ai', icon: serviceIcons.ai, ...t.services.ai },
    { key: 'grc', icon: serviceIcons.grc, ...t.services.grc },
    {
      key: 'changeManagement',
      icon: serviceIcons.changeManagement,
      ...t.services.changeManagement,
      items: (region === 'GCC' && t.services.changeManagement.itemsGCC)
        ? t.services.changeManagement.itemsGCC
        : t.services.changeManagement.items
    },
  ];

  return (
    <section className={styles.services} id="services">
      <div className={styles.services__container}>
        {/* Header */}
        <motion.div
          className={styles.services__header}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.15 }}
        >
          <span className={styles.services__tag}><Sparkles size={14} />{t.services.tag}</span>
          <h2 className={styles.services__title}>{t.services.title}</h2>
          <p className={styles.services__subtitle}>{t.services.subtitle}</p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          className={styles.services__grid}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {services.map((service) => {
            const Icon = service.icon;
            const accentColor = serviceColors[service.key];
            const linkPath = serviceLinks[service.key];

            return (
              <motion.div
                key={service.key}
                variants={cardVariants}
              >
                <Link
                  to={linkPath}
                  className={styles.services__card}
                  style={{ '--accent-color': accentColor } as React.CSSProperties}
                >
                  <div className={styles.services__cardContent}>
                    <div className={styles.services__cardIcon} style={{ color: accentColor }}>
                      <Icon size={28} />
                    </div>
                    <h3 className={styles.services__cardTitle}>{service.title}</h3>
                    <ul className={styles.services__cardList}>
                      {service.items.map((item: string, itemIndex: number) => (
                        <li key={itemIndex} className={styles.services__cardItem}>
                          <span className={styles.services__cardItemDot} style={{ background: accentColor }} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className={styles.services__cardArrow}>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
